import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JsonResult } from '../core/json-result.model';
import { DocumentType } from '../models/master/document-type.model';
import { Basic } from '../core/models/basic.model';
import { ComplaintSubReason } from '../models/master/complaint-sub-reason.model';
import { BookComplaintReason } from '../models/master/book-complaint-reason.model';
import { BookComplaintSubReason } from '../models/master/book-complaint-sub-reason.model';
import { VirtualGroupingType } from '../models/master/virtual-grouping-type.model';
import { StreetType } from '../models/localization/street-type.model';
import { ClientType } from '../models/account/client-type.model';
import { Observable } from 'rxjs';
import { ServiceType } from '../models/localization/service-type.model';
import { EnvironmentService } from '../core/services/environment.service';
import { CommonService } from './common.service';
import { takeUntil } from 'rxjs/operators';
import { HtmlPolicy } from '../models/master/html-policy.model';
import { BillContract } from '../models/Bill-Contrac.model';
import { HtmlPolicyToTV } from 'src/app/models/master/html-policy-to-tv.model';

@Injectable({
  providedIn: 'root'
})
export class MasterService {
  controller: string = "Master";
  constructor(private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private commonService: CommonService) { }

  /**Lista tipos de relaciones con el cliente */
  listRelationshipTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListRelationshipTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url);
  }

  /**Lista tipos de documento */
  listDocumentTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListDocumentTypes`;
    return this.httpClient.get<JsonResult<DocumentType[]>>(url);
  }

  /**Lista motivos de reclamo */
  listComplaintReasons() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListComplaintReasons`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista submotivos de reclamo
   * @param complaintReasonCode código de motivo de reclamo agrupador
   */
  listComplaintSubReasons(complaintReasonCode: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListComplaintSubReasons`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('complaintReasonCode', complaintReasonCode);
    return this.httpClient.get<JsonResult<ComplaintSubReason[]>>(url, { params: httpParams });
  }

  /**
   * Lista motivos de libro de reclamaciones
   * @param complaintTypeCode código de tipo de incidencia
   */
  listBookComplaintReasons(complaintTypeCode: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/listBookComplaintReasons`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('complaintTypeCode', complaintTypeCode);
    return this.httpClient.get<JsonResult<BookComplaintReason[]>>(url, { params: httpParams });
  }

  /**Lista tipos de incidencias de libro de reclamaciones */
  listBookComplaintTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListBookComplaintTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista submotivos de libro de reclamaciones
   * @param complaintReasonCode código de motivo agrupador de libro de reclamaciones
   */
  listBookComplaintSubReasons(complaintReasonCode: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListBookComplaintSubReasons`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('complaintReasonCode', complaintReasonCode);
    return this.httpClient.get<JsonResult<BookComplaintSubReason[]>>(url, { params: httpParams });
  }

  /**Lista tipos de edificio */
  listBuildingTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListBuildingTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**Lista tipos de vivienda */
  listHouseTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListHouseTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**Lista tipos de interior */
  listInteriorTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListInteriorTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**Lista tipos de comite */
  listPhysicalGroupingTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListPhysicalGroupingTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**Lista tipos de conjunto de vivienda  */
  listVirtualGroupingTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListVirtualGroupingTypes`;
    return this.httpClient.get<JsonResult<VirtualGroupingType[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
  /**
   * Lista tipos de calle
   */
  listStreetTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Localization/listStreetTypes`;
    return this.httpClient.get<JsonResult<StreetType[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista tipos de cliente
   */
  listClientTypes() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/listClientTypes`;
    return this.httpClient.get<JsonResult<ClientType[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista opciones de ordenamiento para catálogo
   */
  listCatalogSortOptions() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListCatalogSortOptions`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
  /**
   * Lista tipos de servicio
   */
  listServiceTypes(): Observable<JsonResult<ServiceType[]>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Localization/listServiceTypes`;
    return this.httpClient.get<JsonResult<ServiceType[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista horarios de visita
   */
  listVisitSchedules(): Observable<JsonResult<Basic<string>[]>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/listVisitSchedules`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Lista tipos de solicitudes
   */
  listRequestTypes(): Observable<JsonResult<Basic<string>[]>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ListRequestTypes`;
    return this.httpClient.get<JsonResult<Basic<string>[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  /**
   * Obtiene la estructura html de la política para el @key ingresado 
   * @param key Clave
   */
  getHtmlPolicy(key: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/HtmlPolicy/${key}`;
    return this.httpClient.get<JsonResult<HtmlPolicy>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
   /**
   * Obtiene la estructura html de la política para el @key ingresado 
   * @param key Clave
   */
  GetPaymentBillContract():Observable<JsonResult<BillContract>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/PaymentBillContract`;
    return this.httpClient.get<JsonResult<BillContract>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  getHtmlPolicyFNBToTV() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/HtmlPolicyFNBToTV`;
    return this.httpClient.get<JsonResult<HtmlPolicyToTV>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  getHtmlPolicyGSDToTV() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/HtmlPolicyGSDToTV`;
    return this.httpClient.get<JsonResult<HtmlPolicyToTV>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }  
}
